export type AddInfoMetaLike = {
  cd_flag?: any;
};

// js_addinfo(json/json-string)를 object로 표준화
export const parseJsAddinfoObject = (value: any): Record<string, any> => {
  if (!value) return {};
  if (typeof value === "object" && !Array.isArray(value)) {
    // pg jsonb 컬럼이 { type: "jsonb", value: "{...}" } 형태로 들어오는 경우 지원
    const wrapped = (value as any)?.value;
    if (typeof wrapped === "string") {
      try {
        const parsed = JSON.parse(wrapped);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          return parsed;
        }
      } catch {
        return {};
      }
    }
    return value;
  }
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      return {};
    }
  }
  return {};
};

// sys_addinfo 조회 결과에서 cd_flag 목록 추출
export const extractAddInfoFlags = (rows: any[]): string[] => {
  return (rows || [])
    .map((r: any) => String(r?.cd_flag ?? "").trim())
    .filter((flag: string) => !!flag);
};

// 조회 결과 row의 js_addinfo를 동적 컬럼(cd_flag)으로 펼친다.
export const hydrateRowsWithAddInfo = (rows: any[], flags: string[]): any[] => {
  if (!Array.isArray(rows) || rows.length === 0) return rows || [];
  if (!Array.isArray(flags) || flags.length === 0) return rows;

  return rows.map((row: any) => {
    const jsAddinfo = parseJsAddinfoObject(row?.js_addinfo);
    const hydrated = { ...(row || {}) };
    flags.forEach((flag) => {
      if (Object.prototype.hasOwnProperty.call(jsAddinfo, flag)) {
        hydrated[flag] = jsAddinfo[flag];
      }
    });
    return hydrated;
  });
};

// row의 동적 컬럼값을 js_addinfo로 묶는다. (기존 js_addinfo 값 유지 + 갱신)
export const buildJsAddinfoFromFlags = (
  row: any,
  flags: string[]
): Record<string, any> => {
  const base = { ...parseJsAddinfoObject(row?.js_addinfo) };
  if (!Array.isArray(flags) || flags.length === 0) return base;

  flags.forEach((flag) => {
    const value = row?.[flag];
    if (value === undefined || value === null || value === "") {
      delete base[flag];
    } else {
      base[flag] = value;
    }
  });
  return base;
};

// row의 동적 컬럼값을 js_addinfo로 묶는다. (meta 기준)
export const buildJsAddinfoFromMeta = (
  row: any,
  metaList: AddInfoMetaLike[]
): Record<string, any> => {
  const flags = extractAddInfoFlags(metaList || []);
  return buildJsAddinfoFromFlags(row, flags);
};

export type ExcelAddInfoTarget = {
  // [excel-addinfo] 엑셀 주입 대상 정의
  // target별로 적용할 addinfo flag 목록(예: 헤더 flag, 라인 flag)
  flags?: string[];
  // target별 addinfo json 컬럼 키 목록(예: js_addinfo_h, js_addinfo_l, js_addinfo_d1...)
  jsonKeys?: string[];
  // 적용 우선순위(작을수록 먼저 적용)
  priority?: number;
};

export type ExcelAddInfoConflictPolicy =
  | "keep-original"
  | "first-wins"
  | "last-wins";

export type ExcelAddInfoHydrateOptions = {
  targets?: ExcelAddInfoTarget[];
  // [excel-addinfo] 동일 컬럼 충돌 시 처리 규칙
  // 동일 flag 충돌 시 정책
  // - keep-original: row 원본 값이 있으면 유지
  // - first-wins: 먼저 적용된 값 유지
  // - last-wins: 나중에 적용된 값으로 덮어씀
  conflictPolicy?: ExcelAddInfoConflictPolicy;
};

// [excel-addinfo] 엑셀 다운로드 직전 row 주입 공통 함수
// 엑셀 다운로드 직전에 row에 addinfo(json)를 펼쳐 넣는다.
// - 메뉴마다 addinfo json 컬럼 수가 달라도 jsonKeys 배열로 공통 처리 가능
// - 헤더만/라인만/없음/복합(여러 detail) 케이스를 동일 로직으로 처리
export const hydrateRowsForExcelAddInfo = (
  rows: any[],
  options?: ExcelAddInfoHydrateOptions
): any[] => {
  if (!Array.isArray(rows) || rows.length === 0) return rows || [];

  const targets = Array.isArray(options?.targets) ? options!.targets! : [];
  if (targets.length === 0) return rows.map((r) => ({ ...(r || {}) }));

  const policy: ExcelAddInfoConflictPolicy =
    options?.conflictPolicy ?? "last-wins";

  const sortedTargets = [...targets].sort(
    (a, b) => (a?.priority ?? 999) - (b?.priority ?? 999)
  );

  return rows.map((row: any) => {
    const baseRow = { ...(row || {}) };
    const written = new Set<string>();

    for (const target of sortedTargets) {
      const flags = Array.isArray(target?.flags)
        ? target!.flags!.map((f) => String(f ?? "").trim()).filter(Boolean)
        : [];
      if (flags.length === 0) continue;

      const jsonKeys = Array.isArray(target?.jsonKeys)
        ? target!.jsonKeys!.map((k) => String(k ?? "").trim()).filter(Boolean)
        : [];
      if (jsonKeys.length === 0) continue;

      for (const jsonKey of jsonKeys) {
        const jsObj = parseJsAddinfoObject(baseRow?.[jsonKey]);
        if (!jsObj || typeof jsObj !== "object") continue;

        for (const flag of flags) {
          // flag와 json key의 대소문자 차이를 허용한다.
          const exactKey = Object.prototype.hasOwnProperty.call(jsObj, flag)
            ? flag
            : Object.keys(jsObj).find(
                (k) => String(k).toLowerCase() === String(flag).toLowerCase()
              );
          if (!exactKey) continue;

          const nextVal = jsObj[exactKey];
          if (nextVal === undefined || nextVal === null || nextVal === "")
            continue;

          const hasOriginal =
            baseRow[flag] !== undefined &&
            baseRow[flag] !== null &&
            baseRow[flag] !== "";
          const alreadyWritten = written.has(flag);

          if (policy === "keep-original" && hasOriginal) continue;
          if (policy === "first-wins" && (hasOriginal || alreadyWritten))
            continue;

          baseRow[flag] = nextVal;
          written.add(flag);
        }
      }
    }

    return baseRow;
  });
};
